import { TabStyles, Tooltip, TooltipPosition, TooltipProps } from '@tableau/widgets';
import * as React from 'react';

import { Formatters, Messages } from '../../../../codegen/Localize';
import { classes, cssClass } from '../../../../style';
import { FeatureFlags } from '../../../../ts/FeatureFlags';
import {
  FieldConceptIndexingErrorCode,
  FieldConceptIndexingStatusCode
} from '../../../../ts/ServerApi/requests/GetFieldConceptIndexingStatus';
import { ToasterService, ToastType } from '../../../../ts/services/ToasterService';
import { UpdateValueConcepts } from '../../../apis/UpdateValueConcepts';
import { sectionHeaderClass, sectionTitleClass } from '../../../styles/DefinitionSubspaceSharedStyles';
import { DataType } from '../../../types/DetailEnums';
import { ValueConcept } from '../../../types/ValueConcept';
import { useRadioEditContext } from '../../../utils/RadioEditProvider';
import { DefinitionSubspaceValuesControls } from './DefinitionSubspaceValuesToolbar';
import { ValuesSectionEditButtons } from './ValuesSectionEditButtons';
import { ValuesSectionValuesGrid } from './ValuesSectionValuesGrid';

const MAX_VALUE_COUNT = 10000;

export interface DefinitionSubspaceValuesSectionProps {
  readonly luid: string;
  readonly fieldName: string;
  readonly dataTypes: ReadonlyArray<DataType>;
  readonly valuesList: ReadonlyArray<ValueConcept>;
  readonly valueCount: number;
  readonly fieldConceptIndexingStatusCode: FieldConceptIndexingStatusCode;
  readonly fieldConceptIndexingErrorCode: FieldConceptIndexingErrorCode;
}

const cssPrefix = 'DefinitionSubspaceValuesSection';
const sectionClass = cssClass(cssPrefix, 'section', {flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden'});
const dataGridWrapperClass = cssClass(cssPrefix, 'topRow', {position: 'relative', flex: 1, width: '100%', fontSize:'14px', display: 'flex', flexDirection: 'column', overflowY: 'hidden'});
const titleRealignClass = cssClass(cssPrefix, 'titleRealign', {display: 'inline-block', marginRight: TabStyles.Spacing.DefaultSpace});
const maxRowsMessageClass = cssClass(cssPrefix, 'maxRows', { display: 'inline-block', marginLeft: TabStyles.Spacing.LargeSpace, fontSize:'12px'});

export const  DefinitionSubspaceValuesSection = (props: DefinitionSubspaceValuesSectionProps) => {
  const [filterText, setFilterText] = React.useState('');
  const {isReadonly, setIsEditing, isEditing} = useRadioEditContext();

  const COUNT = Formatters.formatCount(props.valueCount);
  const MAXCOUNT = Formatters.formatCount(MAX_VALUE_COUNT);

  const [editedValueConcepts, setEditedValueConcepts] = React.useState({});
  const searchNotificationText = filterText === '' || props.valueCount <= MAX_VALUE_COUNT ?
      '' :
      Messages.dataRoleDefinitionSubspace_Values_SearchNotification({MAXCOUNT});

  const onValueSynonymSave = () => UpdateValueConcepts(props.luid, Object.values(editedValueConcepts)).then(() => {
    ToasterService.instance.pop({
      type: ToastType.Success,
      text: Messages.dataRoleDefinitionSubspace_Values_Synonyms_Update_ToastSuccess({DATAROLE: props.fieldName})
    });
  }).catch(() => {
    ToasterService.instance.pop({
      type: ToastType.Error,
      text: Messages.dataRoleDefinitionSubspace_Values_Synonyms_Update_ToastError({DATAROLE: props.fieldName})
    });
  });

  return <section className={sectionClass}>
    <header className={sectionHeaderClass}>
      <div>
        {props.dataTypes.indexOf('STRING') === -1 ?
          <Tooltip {...tooltipProps}>
            <h3 className={`${sectionTitleClass} ${titleRealignClass}`}>
              {Messages.dataRoleDefinitionSubspace_Values_Title({COUNT})}
            </h3>
          </Tooltip> :
          <h3 className={`${sectionTitleClass} ${titleRealignClass}`}>
            {Messages.dataRoleDefinitionSubspace_Values_Title({COUNT})}
          </h3>}
        <ValuesSectionEditButtons
          {...{isReadonly, setIsEditing, isEditing}}
          dataTypes={props.dataTypes}
          onValueSynonymsSave={onValueSynonymSave}
          setEditedValueConcepts={setEditedValueConcepts}
          fieldConceptIndexingStatusCode={props.fieldConceptIndexingStatusCode}
          fieldConceptIndexingErrorCode={props.fieldConceptIndexingErrorCode}
        /> <span>{searchNotificationText}</span>
        {FeatureFlags.dataRolesEditValueSynonymsEnabled || props.valueCount <= MAX_VALUE_COUNT ? null :
          <span className={maxRowsMessageClass}>
            {Messages.dataRoleDefinitionSubspace_Values_MaxRowsMessage({MAXCOUNT})}
          </span>}
      </div>
      <span style={{marginRight: TabStyles.Spacing.MediumSpace}}>
        <DefinitionSubspaceValuesControls
        searchText={filterText}
        onChangeSearch={setFilterText}
        />
      </span>
    </header>
    <div className={classes(dataGridWrapperClass, 'tb-datarole-values-list')}>
      <ValuesSectionValuesGrid
        dataTypes={props.dataTypes}
        valuesList={props.valuesList}
        editedValueConcepts={editedValueConcepts}
        setEditedValueConcepts={setEditedValueConcepts}
        valueCount={props.valueCount}
        isEditing={isEditing}
        filterText={filterText}
        resetFilterText={() => setFilterText('')}/>
    </div>
  </section>;
};

const tooltipProps: TooltipProps = {
  getContent: () => <span>{Messages.dataRoleDefinitionSubspace_Values_Title_Tooltip()}</span>,
  position: TooltipPosition.BottomRightCorner
};
